// A following listing's ROOM MAPPING PAGE (docs/plans/2026-10-16-combined-inventory.md §3.20).
//
// THE SERVER DRAWS THE PAGE. Every sentence (what a room sells as, its offset as saved, its rate plans'
// state), every choice the room editor offers (the lead rooms, the plans a plan can soundly copy, the name
// suggestions) comes from ResolveRoomMappingPageMutation. This file only holds what the operator staged
// between saves, turns it into the request body the review and the save both send, and writes a STAGED
// row's cells the way the server writes a saved one. It formats what was typed; it never prices anything.
//
// Vocabulary (locked, §2 rule 1): "Combined inventory", "lead listing", "following listing", "Same room",
// "Added room", "room mapping".

import { formatRateSym } from '@/lib/calendar-utils'
import type { AccountSettingsSharedProps } from '@/types/shared'

export type OffsetKind = 'percentage' | 'absolute'
export type RoomKind = 'same' | 'added'
export type PlanKind = 'copy' | 'follow' | 'none'
export type MappingAccess = 'full' | 'offsets'

/** An offset as typed: % first and the default (CLAUDE.md, the % / £ selector rule); '' is no offset. */
export interface OffsetDraft {
  offsetType: OffsetKind
  offsetValue: string
}

/** What one rate plan of a Same room does. A follow plan's '' offset is 0, the lead plan's own price. */
export interface PlanInstruction {
  kind: PlanKind
  twinPlanId: number | null
  offsetType: OffsetKind
  offsetValue: string
}

export interface MappingLeadPlan {
  id: number
  name: string
  isLead: boolean
}

/** A lead room, as the room editor offers it. */
export interface MappingLeadRoom {
  id: number
  name: string
  /** "Same room as Double" */
  label: string
  /** "Same room as Double · 6 rooms" */
  optionLabel: string
  /** Whether the engine prices it, and which of its plans. */
  priced: boolean
  pricedPlanIds: number[]
  plans: MappingLeadPlan[]
}

export interface MappingPlan {
  id: number
  name: string
  isLead: boolean
  /** Off in the PMS: shown, never offered a choice. */
  active: boolean
  instruction: { kind: PlanKind; twinPlanId: number | null; offsetType: OffsetKind; offsetValue: string | null }
  unpriced: boolean
  /** By lead room id: the plans of that room this plan can soundly copy. */
  copyOptions: Record<string, number[]>
  /** By lead room id: the plan its name suggests, or null. */
  suggested: Record<string, number | null>
}

export type StatusTone = 'ok' | 'warn' | 'muted'

export interface MappingRoom {
  id: number
  name: string
  /** "6 rooms · Max 3" */
  meta: string
  kind: RoomKind
  sameAsRoomTypeId: number | null
  /** The lead room its name suggests (App::RoomMappingSuggestion), or null. */
  suggestedTwinId: number | null
  /** "Same room as Double" or "Added room" */
  sellsAs: string
  /** A Same room's offset as saved, with its label ("+15%", "No offset"); null for an Added room. */
  offset: { type: OffsetKind; value: string | null; label: string } | null
  /** "2 of 2 priced", "1 plan not priced", "Off in your PMS" */
  plansStatus: { label: string; tone: StatusTone }
  /** The room's active lead plan, the one a follow plan follows. */
  leadPlanId: number | null
  plans: MappingPlan[]
}

export interface MappingListingLink {
  id: number
  name: string
  href: string
  current: boolean
}

export interface RoomMappingPageProps extends AccountSettingsSharedProps {
  listing: { id: number; name: string }
  lead: { id: number; name: string }
  currency: string
  access: MappingAccess
  leadRooms: MappingLeadRoom[]
  /** "Added room (not at the lead)" */
  addedOptionLabel: string
  rooms: MappingRoom[]
  /** Server sentences (App::CombinedInventoryNotices). */
  notices: string[]
  savePath: string
  reviewPath: string
  /** The Combined inventory page, or null for someone who cannot open it. */
  combinedInventoryHref: string | null
  /** Every following listing's room mapping, this one marked. */
  listings: MappingListingLink[]
}

/** One room as staged. */
export interface RoomDraft {
  kind: RoomKind
  sameAsRoomTypeId: number | null
  offset: OffsetDraft
  /** By plan id, every ACTIVE plan of a Same room. */
  plans: Record<number, PlanInstruction>
}

export function asOffsetKind(value: string | null | undefined): OffsetKind {
  return value === 'absolute' ? 'absolute' : 'percentage'
}

function storedInstruction(plan: MappingPlan): PlanInstruction {
  return {
    kind: plan.instruction.kind,
    twinPlanId: plan.instruction.twinPlanId,
    offsetType: asOffsetKind(plan.instruction.offsetType),
    offsetValue: plan.instruction.offsetValue ?? '',
  }
}

/** The room as saved, the draft the page starts from. */
export function storedDraft(room: MappingRoom): RoomDraft {
  const plans: Record<number, PlanInstruction> = {}
  if (room.kind === 'same') {
    for (const plan of room.plans) if (plan.active) plans[plan.id] = storedInstruction(plan)
  }
  return {
    kind: room.kind,
    sameAsRoomTypeId: room.sameAsRoomTypeId,
    offset: { offsetType: asOffsetKind(room.offset?.type), offsetValue: room.offset?.value ?? '' },
    plans,
  }
}

/**
 * The instructions a room starts with for a twin: what it does now while the twin is the saved one; for
 * another twin, a plan that follows keeps following (it follows the room's own lead plan), and every other
 * plan takes the name suggestion or nothing. The save resolves a plan the same way.
 */
export function defaultPlans(room: MappingRoom, twinId: number): Record<number, PlanInstruction> {
  const plans: Record<number, PlanInstruction> = {}
  for (const plan of room.plans) {
    if (!plan.active) continue
    const stored = storedInstruction(plan)
    if (room.kind === 'same' && room.sameAsRoomTypeId === twinId) {
      plans[plan.id] = stored
    } else if (room.kind === 'same' && stored.kind === 'follow') {
      plans[plan.id] = stored
    } else {
      const suggested = plan.suggested[String(twinId)] ?? null
      plans[plan.id] = suggested
        ? { kind: 'copy', twinPlanId: suggested, offsetType: 'percentage', offsetValue: '' }
        : { kind: 'none', twinPlanId: null, offsetType: 'percentage', offsetValue: '' }
    }
  }
  return plans
}

/** No offset: blank, or zero. The kind alone is never an offset. */
export function isNoOffset(draft: OffsetDraft): boolean {
  const raw = draft.offsetValue.trim()
  return raw === '' || Number(raw) === 0
}

const MINUS = '−'

/** "+15%", "−£5", "+£7.50", or null for no offset (or a value that is not a number). Formatting only. */
export function offsetLabel(draft: OffsetDraft, sym: string): string | null {
  if (isNoOffset(draft)) return null
  const value = Number(draft.offsetValue.trim())
  if (!Number.isFinite(value)) return null
  const sign = value < 0 ? MINUS : '+'
  const size = Math.abs(value)
  return draft.offsetType === 'absolute' ? `${sign}${formatRateSym(size, sym)}` : `${sign}${size}%`
}

/** A follow plan's offset: 0 reads "0%", the lead plan's own price. */
export function followLabel(draft: OffsetDraft, sym: string): string {
  return offsetLabel(draft, sym) ?? '0%'
}

/** Why a typed offset cannot be applied, or null. The server judges it again on save. */
export function offsetProblem(draft: OffsetDraft): string | null {
  const raw = draft.offsetValue.trim()
  if (raw === '') return null
  const value = Number(raw)
  if (!Number.isFinite(value)) return 'Enter the offset as a number, like 10 or -5'
  if (draft.offsetType === 'percentage' && value <= -100) return 'Enter an offset above -100%, or it would sell for nothing'
  return null
}

function sameOffset(a: OffsetDraft, b: OffsetDraft): boolean {
  if (isNoOffset(a) || isNoOffset(b)) return isNoOffset(a) === isNoOffset(b)
  return a.offsetType === b.offsetType && Number(a.offsetValue) === Number(b.offsetValue)
}

function sameInstruction(a: PlanInstruction | undefined, b: PlanInstruction | undefined): boolean {
  if (!a || !b) return a === b
  if (a.kind !== b.kind) return false
  if (a.kind === 'copy') return a.twinPlanId === b.twinPlanId
  if (a.kind === 'follow') return a.offsetType === b.offsetType && Number(a.offsetValue || 0) === Number(b.offsetValue || 0)
  return true
}

/** What changed on a staged row, against the room as saved. */
export interface RowChanges {
  kind: boolean
  offset: boolean
  plans: boolean
}

export function rowChanges(room: MappingRoom, draft: RoomDraft): RowChanges {
  const stored = storedDraft(room)
  const kind = draft.kind !== stored.kind || draft.sameAsRoomTypeId !== stored.sameAsRoomTypeId
  const offset = draft.kind === 'same' && (kind || !sameOffset(draft.offset, stored.offset))
  const ids = new Set([...Object.keys(draft.plans), ...Object.keys(stored.plans)].map(Number))
  const plans = draft.kind === 'same' && [...ids].some((id) => !sameInstruction(draft.plans[id], stored.plans[id]))
  return { kind, offset: offset && !(kind && isNoOffset(draft.offset)), plans }
}

export function isRowChanged(room: MappingRoom, draft: RoomDraft): boolean {
  const changes = rowChanges(room, draft)
  return changes.kind || changes.offset || changes.plans
}

/** "Same room as Double" or "Added room", for a staged row. */
export function sellsAsLabel(draft: RoomDraft, leadRooms: MappingLeadRoom[]): string {
  if (draft.kind === 'added') return 'Added room'
  const twin = leadRooms.find((room) => room.id === draft.sameAsRoomTypeId)
  return twin ? twin.label : 'Same room'
}

function planCount(count: number): string {
  return `${count} ${count === 1 ? 'plan' : 'plans'}`
}

/**
 * A STAGED row's rate plans, in the words the server uses for a saved one ("2 of 2 priced", "1 plan not
 * priced"). Read off the choices the server listed: a copy is priced when the lead room prices the plan it
 * copies, a follow plan when the room's lead plan is such a copy. A row staged as an Added room is priced on
 * Rooms Setup, so it says that. A count of the operator's choices, never arithmetic on a price.
 */
export function stagedPlansStatus(
  room: MappingRoom,
  draft: RoomDraft,
  leadRooms: MappingLeadRoom[],
): { label: string; tone: StatusTone } {
  const active = room.plans.filter((plan) => plan.active)
  if (active.length === 0) return { label: 'Off in your PMS', tone: 'muted' }
  if (draft.kind === 'added') return { label: 'Priced in Rooms Setup', tone: 'muted' }

  const twin = leadRooms.find((lead) => lead.id === draft.sameAsRoomTypeId)
  if (!twin) return { label: 'Choose a room', tone: 'warn' }
  if (!twin.priced) return { label: `Not priced: ${twin.name} has no price yet`, tone: 'warn' }

  const copies = (plan: MappingPlan) => {
    const instruction = draft.plans[plan.id]
    return instruction?.kind === 'copy' && instruction.twinPlanId != null && twin.pricedPlanIds.includes(instruction.twinPlanId)
  }
  const lead = active.find((plan) => plan.id === room.leadPlanId)
  const leadCopies = lead ? copies(lead) : false
  const unpriced = active.filter((plan) => {
    const instruction = draft.plans[plan.id]
    if (instruction?.kind === 'follow') return !leadCopies
    return !copies(plan)
  }).length

  if (unpriced === 0) return { label: `${active.length} of ${active.length} priced`, tone: 'ok' }
  return { label: `${planCount(unpriced)} not priced`, tone: 'warn' }
}

/** The lead room another room of this listing holds as its twin, as staged: one Same room per lead room. */
export function twinHeldBy(
  rooms: MappingRoom[],
  drafts: Record<number, RoomDraft>,
  twinId: number,
  exceptRoomId: number,
): MappingRoom | null {
  return (
    rooms.find(
      (room) => room.id !== exceptRoomId && drafts[room.id]?.kind === 'same' && drafts[room.id]?.sameAsRoomTypeId === twinId,
    ) ?? null
  )
}

type PlanPayload =
  | { kind: 'copy'; twin_plan_id: number | null }
  | { kind: 'follow'; offset_type: OffsetKind; offset_value: string }
  | { kind: 'none' }

export interface RoomPayload {
  kind: RoomKind
  same_as_room_type_id: number | null
  offset_type?: OffsetKind
  offset_value?: string
  plans?: Record<string, PlanPayload>
}

/**
 * The body the review and the save both send: every room of the listing (the save refuses one left out),
 * each Same room with its offset and an instruction for every active plan, so what the page shows is
 * exactly what is saved.
 */
export function mappingPayload(rooms: MappingRoom[], drafts: Record<number, RoomDraft>): { room_mappings: Record<string, RoomPayload> } {
  const roomMappings: Record<string, RoomPayload> = {}
  for (const room of rooms) {
    const draft = drafts[room.id] ?? storedDraft(room)
    if (draft.kind === 'added') {
      roomMappings[room.id] = { kind: 'added', same_as_room_type_id: null }
      continue
    }
    const plans: Record<string, PlanPayload> = {}
    for (const [planId, instruction] of Object.entries(draft.plans)) {
      plans[planId] =
        instruction.kind === 'copy'
          ? { kind: 'copy', twin_plan_id: instruction.twinPlanId }
          : instruction.kind === 'follow'
            ? { kind: 'follow', offset_type: instruction.offsetType, offset_value: instruction.offsetValue.trim() }
            : { kind: 'none' }
    }
    roomMappings[room.id] = {
      kind: 'same',
      same_as_room_type_id: draft.sameAsRoomTypeId,
      offset_type: draft.offset.offsetType,
      offset_value: draft.offset.offsetValue.trim(),
      plans,
    }
  }
  return { room_mappings: roomMappings }
}
